import { createElement, type ComponentType } from 'react';
import { createRoot } from 'react-dom/client';

type ComponentModule = { default: ComponentType<Record<string, unknown>> };
export type ComponentRegistry = Record<string, () => Promise<unknown>>;

/**
 * `[data-react-component]` を持つ DOM ノードを走査し、対応する React コンポーネントをマウントする。
 *
 * registry（マウント候補の `import.meta.glob` 結果）は entrypoint 側で生成して渡す。
 * これにより community / admin で別ディレクトリ・別バンドルに分離できる（越境マウント・バンドル混入を防ぐ）。
 *
 * - コンポーネント名 = registry の対象ディレクトリ配下の `<名前>.tsx`（default export）
 * - props は `data-react-props`（JSON 文字列）から復元する
 * - 二重マウントは `data-react-mounted` で防止する
 *
 * @param registry 名前解決に使う registry（`import.meta.glob` の結果。spec ではフェイクを渡す）。
 * @param root     走査の起点（既定: document）。後から挿入された部分木を再走査する用途で指定できる。
 */
export function mountReactComponents(
  registry: ComponentRegistry,
  root: ParentNode = document,
): void {
  root.querySelectorAll<HTMLElement>('[data-react-component]').forEach((el) => {
    if (el.dataset.reactMounted) return; // 二重マウントガード
    const name = el.dataset.reactComponent;
    if (!name) return;

    // 1 要素の失敗で同ページの他要素を巻き添えにしないよう、必ず try/catch で囲う。
    try {
      const key = Object.keys(registry).find((k) => k.endsWith(`/${name}.tsx`));
      if (!key) {
        console.error(`[react-mount] unknown component: ${name}`);
        return;
      }

      // フラグはマウント処理の前に立てる。不正 props（JSON.parse 失敗）は恒久エラーなので、
      // 再走査のたびに同じパースエラーでログが氾濫しないようにする。
      el.dataset.reactMounted = 'true';

      const props = el.dataset.reactProps
        ? (JSON.parse(el.dataset.reactProps) as Record<string, unknown>)
        : {};

      void registry[key]()
        .then((mod) => {
          // import 解決までの間に要素が DOM から外れている場合は何もしない（createRoot の無駄打ち防止）。
          if (!el.isConnected) return;
          const Component = (mod as ComponentModule).default;
          if (!Component) {
            throw new Error(`Component ${name} has no default export.`);
          }
          createRoot(el).render(createElement(Component, props));
        })
        .catch((e) => {
          // 読み込みは一時エラー（ネットワーク等）の可能性があるため、フラグを戻して再試行可能にする。
          delete el.dataset.reactMounted;
          console.error(`[react-mount] failed to load ${name}`, e);
        });
    } catch (e) {
      console.error(`[react-mount] failed to mount ${name}`, e);
    }
  });
}
